import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/api/client";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";
import { useWorkspaceBrand } from "@/context/WorkspaceBrandContext";
import { DuplicateWorkOrderModal, WO_STATUS } from "@/pages/WorkOrderEditor";
import { printAgreement } from "@/lib/agreementPdf";
import { Plus, Copy, FilePdf, PencilSimple, Trash, FileText, Paperclip } from "@phosphor-icons/react";

function StatusBadge({ w }) {
  const s = WO_STATUS[w.status || "draft"];
  return (
    <span className={`dos-badge text-[10px] inline-flex items-center gap-1 ${s.cls}`} data-testid={`gen-wo-status-${w.id}`}>
      <s.Icon size={10} /> {s.label}{w.signed_file_id && <Paperclip size={10} />}
    </span>
  );
}

export function GeneratedWorkOrders() {
  const nav = useNavigate();
  const confirm = useConfirm();
  const { workspace } = useWorkspaceBrand();
  const [list, setList] = useState([]);
  const [creating, setCreating] = useState(false);
  const [dup, setDup] = useState(null);

  const load = () => api.get("/agreements").then((r) => setList(r.data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true);
    try { const { data } = await api.post("/agreements"); nav(`/business/work-orders/${data.id}`); }
    catch (e) { toast.error(formatError(e)); setCreating(false); }
  };
  const pdf = async (w) => {
    try { const { data } = await api.get(`/agreements/${w.id}`); printAgreement(data, workspace); }
    catch (e) { toast.error(formatError(e)); }
  };
  const del = async (w) => {
    if (!(await confirm({ title: `Delete ${w.wo_number}?`, message: `"${w.title}" will be permanently removed.`, confirmLabel: "Delete", tone: "danger" }))) return;
    try { await api.delete(`/agreements/${w.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="mb-8" data-testid="generated-wo-section">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div>
          <div className="font-display font-semibold text-neutral-900">Created in the App</div>
          <div className="text-xs text-neutral-500">Write agreements clause by clause, then generate a PDF on your letterhead.</div>
        </div>
        <button onClick={create} disabled={creating} data-testid="create-wo-btn" className="dos-btn dos-btn-primary"><Plus size={14} /> {creating ? "Creating…" : "Create Work Order"}</button>
      </div>
      <div className="dos-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-neutral-500 border-b border-neutral-200">
              <th className="px-4 py-3">WO #</th><th className="px-4 py-3">Title / Project</th><th className="px-4 py-3">Client</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Updated</th><th className="px-4 py-3 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {list.map((w) => (
              <tr key={w.id} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50 cursor-pointer" onClick={() => nav(`/business/work-orders/${w.id}`)} data-testid={`gen-wo-row-${w.id}`}>
                <td className="px-4 py-3 font-mono text-xs">{w.wo_number}</td>
                <td className="px-4 py-3"><div className="font-medium text-neutral-900">{w.title}</div><div className="text-xs text-neutral-500 truncate max-w-md">{w.project_name}</div></td>
                <td className="px-4 py-3 text-xs text-neutral-600">{w.client_name || "—"}</td>
                <td className="px-4 py-3"><StatusBadge w={w} /></td>
                <td className="px-4 py-3 font-mono text-xs text-neutral-500">{(w.updated_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => nav(`/business/work-orders/${w.id}`)} data-testid={`gen-wo-edit-${w.id}`} title="Edit" className="p-1.5 rounded hover:bg-neutral-100"><PencilSimple size={14} /></button>
                  <button onClick={() => setDup(w)} data-testid={`gen-wo-dup-${w.id}`} title="Duplicate & rename" className="p-1.5 rounded hover:bg-neutral-100"><Copy size={14} /></button>
                  <button onClick={() => pdf(w)} data-testid={`gen-wo-pdf-${w.id}`} title="Generate PDF" className="p-1.5 rounded hover:bg-neutral-100"><FilePdf size={14} /></button>
                  <button onClick={() => del(w)} data-testid={`gen-wo-del-${w.id}`} title="Delete" className="p-1.5 rounded hover:bg-red-50 text-neutral-400 hover:text-red-500"><Trash size={14} /></button>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-neutral-500 text-sm"><FileText size={20} className="mx-auto mb-2 text-neutral-300" />No work orders created yet. Click <b>Create Work Order</b> to start from your standard agreement.</td></tr>}
          </tbody>
        </table>
      </div>
      {dup && <DuplicateWorkOrderModal source={dup} onClose={() => setDup(null)} onDone={(d) => { setDup(null); nav(`/business/work-orders/${d.id}`); }} />}
    </div>
  );
}
